import { useEffect, useRef } from 'react';
import { WALK_FILTERS } from './constants';
import { addWalkSegment } from './walkStorage';

function haversineMeters(a, b) {
  const radius = 6371000;
  const toRad = (value) => value * Math.PI / 180;
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const deltaLat = toRad(b.lat - a.lat);
  const deltaLng = toRad(b.lng - a.lng);
  const value = Math.sin(deltaLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
  return radius * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
}

export default function useWalkSession({ enabled, uid, position }) {
  const previousRef = useRef(null);

  useEffect(() => {
    if (!enabled || !uid) previousRef.current = null;
  }, [enabled, uid]);

  useEffect(() => {
    const resetPrevious = () => {
      if (document.hidden) previousRef.current = null;
    };
    document.addEventListener('visibilitychange', resetPrevious);
    return () => document.removeEventListener('visibilitychange', resetPrevious);
  }, []);

  useEffect(() => {
    if (!enabled || !uid || document.hidden ||
      position?.lat == null || position?.lng == null) return;

    const current = {
      lat: Number(position.lat),
      lng: Number(position.lng),
      acc: Number(position.acc),
      speed: Number(position.speed),
      timestamp: Number(position.timestamp) || Date.now()
    };
    if (!Number.isFinite(current.lat) || !Number.isFinite(current.lng)) return;
    if (Number.isFinite(current.acc) && current.acc > WALK_FILTERS.maxAccuracyM) return;

    const previous = previousRef.current;
    if (!previous) {
      previousRef.current = current;
      return;
    }

    const intervalSec = (current.timestamp - previous.timestamp) / 1000;
    if (intervalSec <= 0) return;
    if (intervalSec > WALK_FILTERS.maxIntervalSec) {
      previousRef.current = current;
      return;
    }

    const distanceM = haversineMeters(previous, current);
    if (distanceM < WALK_FILTERS.minSegmentM) return;

    const segmentSpeed = distanceM / intervalSec;
    const reportedSpeed = Math.max(current.speed || 0, previous.speed || 0);
    const tooFast = segmentSpeed > WALK_FILTERS.maxSpeedMps || reportedSpeed > WALK_FILTERS.maxSpeedMps;
    if (tooFast) {
      previousRef.current = current;
      return;
    }
    if (segmentSpeed < WALK_FILTERS.minSpeedMps) return;

    previousRef.current = current;
    addWalkSegment(uid, distanceM, intervalSec);
  }, [enabled, uid, position]);
}
